import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Alert, CircularProgress, List, ListItem,
  ListItemText, ListItemSecondaryAction,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon,
  AddCircleOutline as AddItemIcon,
  RemoveCircleOutline as RemoveItemIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';

const ReleaseNotes = () => {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formVersion, setFormVersion] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formItems, setFormItems] = useState(['']);
  const [formPublished, setFormPublished] = useState(false);

  const loadReleases = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.listReleases();
      setReleases(res.data.releases);
    } catch (err) {
      setError('Failed to load releases');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReleases(); }, [loadReleases]);

  const openCreate = () => {
    setEditing(null);
    setFormVersion('');
    setFormTitle('');
    setFormItems(['']);
    setFormPublished(false);
    setDialogOpen(true);
  };

  const openEdit = (release) => {
    setEditing(release);
    setFormVersion(release.version);
    setFormTitle(release.title);
    const items = Array.isArray(release.items) ? release.items : [];
    setFormItems(items.length > 0 ? items : ['']);
    setFormPublished(release.is_published);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formVersion.trim() || !formTitle.trim()) {
      setError('Version and title are required');
      return;
    }

    const filteredItems = formItems.filter(item => item.trim() !== '');
    if (filteredItems.length === 0) {
      setError('Add at least one bullet point');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const data = {
        version: formVersion.trim(),
        title: formTitle.trim(),
        items: filteredItems,
        is_published: formPublished,
      };

      if (editing) {
        await adminAPI.updateRelease(editing.id, data);
        setSuccess('Release updated');
      } else {
        await adminAPI.createRelease(data);
        setSuccess('Release created');
      }
      setDialogOpen(false);
      loadReleases();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save release');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminAPI.deleteRelease(id);
      setSuccess('Release deleted');
      setDeleteConfirm(null);
      loadReleases();
    } catch (err) {
      setError('Failed to delete release');
    }
  };

  const handleTogglePublish = async (release) => {
    try {
      await adminAPI.updateRelease(release.id, { is_published: !release.is_published });
      setSuccess(release.is_published ? 'Release unpublished' : 'Release published');
      loadReleases();
    } catch (err) {
      setError('Failed to update release');
    }
  };

  const addBulletItem = () => setFormItems([...formItems, '']);

  const removeBulletItem = (index) => {
    if (formItems.length <= 1) return;
    setFormItems(formItems.filter((_, i) => i !== index));
  };

  const updateBulletItem = (index, value) => {
    const updated = [...formItems];
    updated[index] = value;
    setFormItems(updated);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>Release Notes</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Release
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Published</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {releases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" py={3}>
                    No releases yet. Create your first one!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              releases.map((release) => {
                const items = Array.isArray(release.items) ? release.items : [];
                return (
                  <TableRow key={release.id}>
                    <TableCell>
                      <Typography fontWeight={600} fontFamily="monospace">
                        v{release.version}
                      </Typography>
                    </TableCell>
                    <TableCell>{release.title}</TableCell>
                    <TableCell>{items.length} item{items.length !== 1 ? 's' : ''}</TableCell>
                    <TableCell>
                      <Chip
                        label={release.is_published ? 'Published' : 'Draft'}
                        color={release.is_published ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {release.published_at
                        ? new Date(release.published_at).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleTogglePublish(release)}
                        title={release.is_published ? 'Unpublish' : 'Publish'}
                      >
                        {release.is_published ? <UnpublishIcon /> : <PublishIcon />}
                      </IconButton>
                      <IconButton size="small" onClick={() => openEdit(release)} title="Edit">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteConfirm(release)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Release' : 'New Release'}</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} mt={1} mb={2}>
            <TextField
              label="Version"
              value={formVersion}
              onChange={(e) => setFormVersion(e.target.value)}
              placeholder="1.0.0-beta.1"
              size="small"
              sx={{ width: 180 }}
            />
            <TextField
              label="Title"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="What's new in this release"
              size="small"
              fullWidth
            />
          </Box>

          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            Bullet Points
          </Typography>
          <List dense disablePadding>
            {formItems.map((item, index) => (
              <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                <Box display="flex" alignItems="center" gap={1} width="100%">
                  <Typography color="text.secondary" sx={{ minWidth: 16 }}>•</Typography>
                  <TextField
                    value={item}
                    onChange={(e) => updateBulletItem(index, e.target.value)}
                    placeholder="Describe a change..."
                    size="small"
                    fullWidth
                    multiline
                    maxRows={3}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeBulletItem(index)}
                    disabled={formItems.length <= 1}
                    color="error"
                  >
                    <RemoveItemIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
          <Button
            size="small"
            startIcon={<AddItemIcon />}
            onClick={addBulletItem}
            sx={{ mt: 0.5 }}
          >
            Add Bullet Point
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={() => { setFormPublished(false); handleSave(); }}
            disabled={saving}
          >
            Save as Draft
          </Button>
          <Button
            variant="contained"
            onClick={() => { setFormPublished(true); handleSave(); }}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : (editing ? 'Save & Publish' : 'Publish')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Release?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>v{deleteConfirm?.version}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm.id)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReleaseNotes;
